import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateBankDetails, setBankDetailsCompleted, setLoading } from '@/store/slices/profileSlice';
import { CheckCircle } from 'lucide-react';

const BankDetailsTabForm: React.FC = () => {
    const dispatch = useDispatch();
    const { bankDetails, loading } = useSelector((state: RootState) => state.profile);
    const { toast } = useToast();
    const [isEditing, setIsEditing] = useState(false);

    const [localData, setLocalData] = useState(bankDetails);

    const handleInputChange = (field: string, value: string) => {
        const updatedData = { ...localData, [field]: value };
        setLocalData(updatedData);
        dispatch(updateBankDetails({ [field]: value }));
    };

    const validateIFSC = (ifsc: string): boolean => {
        return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);
    };

    const validateUPI = (upi: string): boolean => {
        return /^[\w\.\-_]{3,}@[\w\-_]{2,}$/.test(upi);
    };

    const validateForm = (): boolean => {
        if (localData.ifsc && !validateIFSC(localData.ifsc)) {
            toast({ title: 'Error', description: 'Please enter a valid IFSC code', variant: 'destructive' });
            return false;
        }

        if (localData.upiId && !validateUPI(localData.upiId)) {
            toast({ title: 'Error', description: 'Please enter a valid UPI ID', variant: 'destructive' });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        dispatch(setLoading(true));
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            dispatch(setBankDetailsCompleted(true));
            toast({ title: 'Success', description: 'Bank details saved successfully!' });
        } catch (err) {
            toast({ title: 'Error', description: 'Failed to save bank details', variant: 'destructive' });
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name</Label>
                <Input
                    disabled={!isEditing}
                    id="bankName"
                    value={localData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    placeholder="Enter bank name"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="accountHolderName">Account Holder Name</Label>
                <Input
                    disabled={!isEditing}

                    id="accountHolderName"
                    value={localData.accountHolderName}
                    onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                    placeholder="Enter account holder name"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input
                    disabled={!isEditing}

                    id="accountNumber"
                    value={localData.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    placeholder="Enter account number"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="ifscCode">IFSC Code</Label>
                <Input
                    disabled={!isEditing}

                    id="ifscCode"
                    value={localData.ifsc}
                    onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                />
                {localData.ifsc && !validateIFSC(localData.ifsc) && (
                    <p className="text-sm text-destructive">Invalid IFSC code format</p>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="upiId">UPI ID</Label>
                <Input
                    disabled={!isEditing}

                    id="upiId"
                    value={localData.upiId}
                    onChange={(e) => handleInputChange('upiId', e.target.value)}
                    placeholder="Enter UPI ID (e.g., user@paytm)"
                />
                {localData.upiId && !validateUPI(localData.upiId) && (
                    <p className="text-sm text-destructive">Invalid UPI ID format</p>
                )}
            </div>

            <div className="flex items-center space-x-2">
                <CheckCircle
                    className={`h-5 w-5 ${localData.isVerified ? 'text-green-500' : 'text-muted-foreground'}`}
                />
                <Label className={localData.isVerified ? 'text-green-500' : 'text-muted-foreground'}>
                    Bank Details Verified {localData.isVerified ? '✓' : '(Not Verified)'}
                </Label>
            </div>

            {/* <div className="pt-4">
                <Button
                    onClick={handleSubmit}
                    className="w-full"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Bank Details'}
                </Button>
            </div> */}


            <div className="flex gap-4 pt-6">
                <p className='text-muted-foreground'>Note: To update bank account please conect with <span className='font-bold'> support@bookyourzone.com</span></p>
                {/* {!isEditing ? (
                    <Button
                        onClick={() => setIsEditing(true)}
                        className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium w-full"
                    >
                        Edit Bank Deatils
                    </Button>
                ) : (


                    <>
                        <Button
                            onClick={handleSubmit}
                            disabled={loading}

                            className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium"
                        >

                            {loading ? 'Saving...' : 'Save Bank Details'}

                        </Button>
                        <Button
                            onClick={handleCancel}
                            variant="none"
                            className="flex-1 h-11 border-2 border-flup-hairline-2 text-flup-ink-2 font-medium hover:bg-flup-surface-alt"
                        >
                            Cancel
                        </Button>
                    </>
                )} */}
            </div>
        </div>
    );
};

export default BankDetailsTabForm;